import { useEffect, useRef, useState } from 'react';
import { Tabs } from '@/components/common/Tabs';
import { PageHeader } from '@/components/common/PageHeader';
import { usePermission } from '@/permissions/usePermission';
import { GlobalSymbolsTab } from './GlobalSymbolsTab';
import { RequestHistoryTab } from './RequestHistoryTab';
import { PendingRequestsModal } from './PendingRequestsModal';
import { GlobalSymbolsPermissions } from './permissions';
import { useFormData, usePendingSymbolRequests } from './api';

type TabKey = 'global-symbols' | 'request-history';

export function GlobalSymbolsPage() {
  const can = usePermission();
  const canView = can(GlobalSymbolsPermissions.view);
  const canApprove = can(GlobalSymbolsPermissions.approve);
  const canReject = can(GlobalSymbolsPermissions.reject);

  const [active, setActive] = useState<TabKey>('global-symbols');
  const [showPopup, setShowPopup] = useState(false);
  // Auto-show the pending-requests popup only ONCE per visit (legacy behaviour).
  // Without this guard it would re-open on every background refetch and keep
  // blocking the page after the user closed it.
  const autoShownRef = useRef(false);

  const pendingQuery = usePendingSymbolRequests(canView);
  const formDataQuery = useFormData();

  const pending = pendingQuery.data ?? [];

  useEffect(() => {
    if (!autoShownRef.current && canView && pending.length > 0 && formDataQuery.data != null) {
      autoShownRef.current = true;
      setShowPopup(true);
    }
  }, [canView, pending.length, formDataQuery.data]);

  return (
    <div>
      <PageHeader title="Global Symbols" breadcrumb={['Home', 'Global Symbols']} />

      <div className="card custom-card border-0 shadow-none">
        <Tabs
          tabs={[
            { key: 'global-symbols', title: 'Global Symbols' },
            { key: 'request-history', title: 'Request History' },
          ]}
          active={active}
          onChange={(k) => setActive(k as TabKey)}
          className="mb-3"
        />

        <div className="tab-content text-muted">
          {active === 'global-symbols' && (
            <div className="tab-pane active" role="tabpanel">
              <GlobalSymbolsTab />
            </div>
          )}
          {active === 'request-history' && (
            <div className="tab-pane active" role="tabpanel">
              <RequestHistoryTab />
            </div>
          )}
        </div>
      </div>

      {showPopup && formDataQuery.data && (
        <PendingRequestsModal
          show
          requests={pending}
          formData={formDataQuery.data}
          canApprove={canApprove}
          canReject={canReject}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}
